import { create } from 'zustand';

import { Seller, SellerState, SellerUpdatePayload, SellerPagination } from 'src/types/sellers';
import { sellersRepository } from 'src/repositories/sellers/sellers.repository';

type Actions = {
  setOrder: (order: 'desc' | 'asc') => void;
  setOrderBy: (orderBy: string) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setLoading: (loading: boolean) => void;
  setTotal: (total: number) => void;
  fetchSellers: (params?: SellerPagination) => Promise<void>;
  findSellerById: (id: string) => Promise<Seller>;
  createSeller: (seller: SellerUpdatePayload) => Promise<void>;
  updateSeller: (id: string, updatedSeller: SellerUpdatePayload) => Promise<void>;
  deleteSeller: (id: string) => Promise<void>;
};

const initialValues: SellerState = {
  sellers: [],
  loading: false,
  error: null,
  order: 'desc',
  orderBy: 'createdAt',
  page: 1,
  perPage: 10,
  total: 0,
};

export const useSellerStore = create<SellerState & Actions>((set) => ({
  ...initialValues,

  setOrder: (order: 'desc' | 'asc') => set({ order }),

  setOrderBy: (orderBy: string) => set({ orderBy }),

  setPage: (page: number) => set({ page }),

  setPerPage: (perPage: number) => set({ perPage, page: 1 }),

  setLoading: (loading: boolean) => set({ loading }),

  setTotal: (total: number) => set({ total }),

  fetchSellers: async (params?: SellerPagination) => {
    set({ loading: true, error: null });
    try {
      const currentState = useSellerStore.getState();
      const queryParams: SellerPagination = params || {
        page: currentState.page,
        perPage: currentState.perPage,
        orderBy: currentState.orderBy,
        order: currentState.order,
      };

      const response = await sellersRepository.getAll(queryParams);
      set({ sellers: response.items ?? [], total: response.pagination?.totalCount ?? 0, loading: false });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
    }
  },

  findSellerById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const seller = await sellersRepository.getById(id);
      set({ loading: false });
      return seller;
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
      throw error;
    }
  },

  createSeller: async (seller: SellerUpdatePayload) => {
    set({ loading: true, error: null });
    try {
      await sellersRepository.create(seller);
      await useSellerStore.getState().fetchSellers();
      set({ loading: false });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
      throw error;
    }
  },

  updateSeller: async (id: string, updatedSeller: SellerUpdatePayload) => {
    set({ loading: true, error: null });
    try {
      await sellersRepository.update(id, updatedSeller);
      await useSellerStore.getState().fetchSellers();
      set({ loading: false });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
      throw error;
    }
  },

  deleteSeller: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await sellersRepository.delete(id);
      await useSellerStore.getState().fetchSellers();
      set({ loading: false });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
      throw error;
    }
  },
}));
